/**
 * NewDatabaseModal — F 196A Phase A
 *
 * The create affordance the databases surface never had.
 *
 * Context, because it is not obvious from the code: `createDefaultDatabase()`
 * in core/workspace/databaseStorage already builds a full Database (5-column
 * schema, a Board view and a List view, no items) and WorkspaceContext's
 * `createPage(title, parentId, 'database')` already persists it. The whole
 * create path existed. What did not exist was anything a person could click —
 * the gallery card that offers a 'database' template lives in `HomeView`,
 * and `HomeView` is defined in ActiveWorkspace.tsx and never rendered, so
 * the one create entry point in the product is unreachable.
 *
 * This modal is the reachable entry point. It follows the same pattern as
 * QuickCaptureModal (Modal + form + Enter submits + Esc dismisses) so it
 * feels like the rest of the product.
 *
 * On layout: `createDefaultDatabase` seeds kanban + list views and this
 * modal does NOT override them. The chosen layout is recorded on the page's
 * coverLayout, which DatabaseViewWrapper already reads and passes to
 * DatabaseView as `pageCoverLayout` — that is the existing channel for "how
 * should this database present itself", so using it invents nothing.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Button, TextInput, Stack, Text } from '@mantine/core';
import { Modal } from '../../primitives/Modal';
import { VIEW_LAYOUTS, VIEW_LAYOUT_DESCRIPTIONS } from '../../../core/views/viewConfig';
import type { ViewLayout } from '../../../core/views/viewConfig';

export interface NewDatabaseModalProps {
  opened: boolean;
  onClose: () => void;
  /** name, description, chosen layout */
  onCreate: (name: string, description: string, layout: ViewLayout) => void | Promise<void>;
}

/** Default layout. 'list' is the least surprising first view for a new,
 *  empty database — a board implies columns the operator has not defined. */
const DEFAULT_LAYOUT: ViewLayout = 'list';

export const NewDatabaseModal: React.FC<NewDatabaseModalProps> = ({ opened, onClose, onCreate }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [layout, setLayout] = useState<ViewLayout>(DEFAULT_LAYOUT);
  const [busy, setBusy] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);

  // Reset on open so a cancelled create does not leak into the next one.
  useEffect(() => {
    if (opened) {
      setName('');
      setDescription('');
      setLayout(DEFAULT_LAYOUT);
      setBusy(false);
    }
  }, [opened]);

  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await onCreate(name.trim() || 'Untitled Database', description.trim(), layout);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      maxWidth={480}
      padding={20}
      borderRadius={12}
      zIndex={1000}
      title="New database"
      header={<div className="fc-new-database__header">New database</div>}
    >
      <form onSubmit={submit}>
        <Stack gap="sm">
          <TextInput
            id="fc-new-database-name"
            ref={nameRef}
            label="Name"
            placeholder="e.g. Roadmap"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            autoComplete="off"
            data-testid="fc-new-database-name"
            autoFocus
          />
          <TextInput
            id="fc-new-database-description"
            label="Description"
            placeholder="Optional"
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
            autoComplete="off"
            data-testid="fc-new-database-description"
          />
          <label
            htmlFor="fc-new-database-layout"
            style={{ fontSize: 14 }}
            data-testid="fc-new-database-layout-label"
          >
            Default layout
          </label>
          <select
            id="fc-new-database-layout"
            value={layout}
            onChange={(e) => setLayout(e.currentTarget.value as ViewLayout)}
            data-testid="fc-new-database-layout"
            style={{
              padding: '8px 10px',
              borderRadius: 6,
              border: '1px solid var(--fc-border-primary, #e9e9e7)',
              background: 'var(--fc-bg-primary)',
              color: 'var(--fc-text-primary)',
              width: '100%',
            }}
          >
            {VIEW_LAYOUTS.map((l) => (
              <option key={l} value={l}>
                {l} — {VIEW_LAYOUT_DESCRIPTIONS[l] ?? ''}
              </option>
            ))}
          </select>
          <Text size="xs" c="dimmed">
            A new database starts empty with Title, Date, Status, Tag and Finished columns.
          </Text>
          <Button type="submit" loading={busy} data-testid="fc-new-database-submit">
            Create database
          </Button>
          <Text size="xs" c="dimmed">
            <kbd>Enter</kbd> to create · <kbd>Esc</kbd> to dismiss
          </Text>
        </Stack>
      </form>
    </Modal>
  );
};

export default NewDatabaseModal;